import * as EVENTS from '../../common/constant';
import { createElement, isNullOrUndefined as isNOU, detach, addClass, Browser } from '@syncfusion/ej2-base';
import { PASTE_SOURCE } from '../base/constant';
import { InsertMethods } from './insert-methods';
/**
 * PasteCleanup for MsWord content
 *
 * @hidden
 * @private
 */
export class MsWordPaste {
    /**
     * Initializes a new instance of the MsWordPaste class
     *
     * @param {EditorManager} parent - The parent editor manager instance
     * @returns {void} - No return value
     */
    constructor(parent) {
        this.olData = [
            'decimal',
            'decimal-leading-zero',
            'lower-alpha',
            'lower-roman',
            'upper-alpha',
            'upper-roman',
            'lower-greek'
        ];
        this.ulData = [
            'disc',
            'square',
            'circle',
            'disc',
            'square',
            'circle'
        ];
        /** List of HTML node names that should not be ignored during cleanup */
        this.ignorableNodes = ['A', 'APPLET', 'B', 'BLOCKQUOTE', 'BR',
            'BUTTON', 'CENTER', 'CODE', 'COL', 'COLGROUP', 'DD', 'DEL', 'DFN', 'DIR', 'DIV',
            'DL', 'DT', 'EM', 'FIELDSET', 'FONT', 'FORM', 'FRAME', 'FRAMESET', 'H1', 'H2',
            'H3', 'H4', 'H5', 'H6', 'HR', 'I', 'IMG', 'IFRAME', 'INPUT', 'INS', 'LABEL',
            'LI', 'OL', 'OPTION', 'P', 'PARAM', 'PRE', 'Q', 'S', 'SELECT', 'SPAN', 'STRIKE',
            'STRONG', 'SUB', 'SUP', 'TABLE', 'TBODY', 'TD', 'TEXTAREA', 'TFOOT', 'TH',
            'THEAD', 'TITLE', 'TR', 'TT', 'U', 'UL'];
        /** List of HTML block node names */
        this.blockNode = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'address', 'blockquote', 'button', 'center', 'dd', 'dir', 'dl', 'dt', 'fieldset',
            'frameset', 'hr', 'iframe', 'isindex', 'li', 'map', 'menu', 'noframes', 'noscript',
            'object', 'ol', 'pre', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul',
            'header', 'article', 'nav', 'footer', 'section', 'aside', 'main', 'figure', 'figcaption'];
        this.borderStyle = ['border-top', 'border-right', 'border-bottom', 'border-left'];
        this.upperRomanNumber = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX',
            'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'];
        this.lowerRomanNumber = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix',
            'x', 'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx'];
        this.lowerGreekNumber = ['α', 'β', 'γ', 'δ', 'ε', 'ζ', 'η', 'θ', 'ι', 'κ', 'λ',
            'μ', 'ν', 'ξ', 'ο', 'π', 'ρ', 'σ', 'τ', 'υ', 'φ', 'χ', 'ψ', 'ω'];
        this.removableElements = ['o:p', 'style', 'w:sdt'];
        this.listContents = [];
        this.cropImageDimensions = [];
        this.parent = parent;
        this.addEventListener();
    }
    addEventListener() {
        this.parent.observer.on(EVENTS.MS_WORD_CLEANUP_PLUGIN, this.wordCleanup, this);
        this.parent.observer.on(EVENTS.INTERNAL_DESTROY, this.destroy, this);
    }
    removeEventListener() {
        this.parent.observer.off(EVENTS.MS_WORD_CLEANUP_PLUGIN, this.wordCleanup);
        this.parent.observer.off(EVENTS.INTERNAL_DESTROY, this.destroy);
    }
    /* Cleans up MS Word content from clipboard data */
    wordCleanup(notifyArgs) {
        const wordPasteStyleConfig = !isNOU(notifyArgs.allowedStylePropertiesArray) ?
            notifyArgs.allowedStylePropertiesArray : [];
        let listNodes = [];
        let clipboardHtmlContent = !isNOU(notifyArgs.text) ? notifyArgs.text :
            notifyArgs.args.clipboardData.getData('text/HTML');
        const rtfData = notifyArgs.args.clipboardData.getData('text/rtf');
        const clipboardDataElement = createElement('p');
        clipboardDataElement.setAttribute('id', 'MSWord-Content');
        clipboardDataElement.innerHTML = clipboardHtmlContent;
        this.addDoubleBr(clipboardDataElement);
        const msoClassSingleQuotePattern = /class='?Mso|style='[^ ]*\bmso-/i;
        const msoClassDoubleQuotePattern = /class="?Mso|style="[^ ]*\bmso-/i;
        const msoComplexPattern = /(class="?Mso|class='?Mso|class="?Xl|class='?Xl|class=Xl|style="[^"]*\bmso-|style='[^']*\bmso-|w:WordDocument)/gi;
        const msoWidthSourcePattern = /style='mso-width-source:/i;
        const contentSource = this.findSource(clipboardDataElement);
        if (msoClassSingleQuotePattern.test(clipboardHtmlContent) || msoClassDoubleQuotePattern.test(clipboardHtmlContent) ||
            msoComplexPattern.test(clipboardHtmlContent) || msoWidthSourcePattern.test(clipboardHtmlContent)) {
            clipboardHtmlContent = clipboardHtmlContent.replace(/<img[^>]+>/i, '');
            this.addListClass(clipboardDataElement);
            listNodes = this.listCleanUp(clipboardDataElement, listNodes);
            if (!isNOU(listNodes[0]) && listNodes[0].parentElement.tagName !== 'UL' &&
                listNodes[0].parentElement.tagName !== 'OL') {
                this.listConverter(listNodes);
            }
            this.imageConversion(clipboardDataElement, rtfData);
            this.cleanList(clipboardDataElement, 'UL');
            this.cleanList(clipboardDataElement, 'OL');
            this.processTableBorder(clipboardDataElement);
            this.styleCorrection(clipboardDataElement, wordPasteStyleConfig);
            this.removingComments(clipboardDataElement);
            this.removeUnwantedElements(clipboardDataElement);
            this.removeEmptyElements(clipboardDataElement);
            this.removeEmptyAnchorTag(clipboardDataElement);
            this.breakLineAddition(clipboardDataElement);
            this.processMargin(clipboardDataElement);
            this.removeClassName(clipboardDataElement);
            if (msoWidthSourcePattern.test(clipboardHtmlContent)) {
                this.addTableBorderClass(clipboardDataElement);
            }
            notifyArgs.callBack(clipboardDataElement.innerHTML, this.cropImageDimensions, contentSource);
        }
        else {
            if (contentSource === PASTE_SOURCE[2]) {
                this.handleOneNoteContent(clipboardDataElement);
            }
            this.removeEmptyMetaTags(clipboardDataElement);
            notifyArgs.callBack(clipboardDataElement.innerHTML, null, contentSource);
        }
    }
    /* Adds double line breaks for Apple-interchange-newline elements in Chrome. */
    addDoubleBr(clipboardDataElement) {
        const newlineElement = clipboardDataElement.querySelector('.Apple-interchange-newline');
        const isValidNewline = !isNOU(newlineElement) && Browser.userAgent.indexOf('Chrome') !== -1 &&
            newlineElement.parentElement.nodeName === 'P' && clipboardDataElement !== newlineElement.parentElement;
        if (isValidNewline) {
            for (let i = 0; i < clipboardDataElement.childNodes.length; i++) {
                const currentNode = clipboardDataElement.childNodes[i];
                const isStartFragment = currentNode.nodeType === Node.COMMENT_NODE &&
                    currentNode.nodeValue.indexOf('StartFragment') !== -1;
                if (isStartFragment) {
                    const paragraphElement = createElement('p');
                    paragraphElement.innerHTML = '<br>';
                    const parentStyles = newlineElement.parentElement.getAttribute('style');
                    const currentStyles = paragraphElement.getAttribute('style') || '';
                    const combinedStyles = currentStyles + parentStyles;
                    paragraphElement.setAttribute('style', combinedStyles);
                    clipboardDataElement.insertBefore(paragraphElement, currentNode.nextSibling);
                    detach(newlineElement);
                    break;
                }
            }
        }
    }
    /* Cleans list elements by removing div elements and restructuring the list */
    cleanList(clipboardDataElement, listTagName) {
        const divElements = clipboardDataElement.querySelectorAll(listTagName + ' div');
        for (let i = divElements.length - 1; i >= 0; i--) {
            const currentDiv = divElements[i];
            const parentNode = currentDiv.parentNode;
            // Move all children of the div to its parent
            while (currentDiv.firstChild) {
                parentNode.insertBefore(currentDiv.firstChild, currentDiv);
            }
            // Find the closest list element and insert the div after it
            const closestListElement = this.findClosestListElem(currentDiv);
            if (closestListElement) {
                this.insertAfter(currentDiv, closestListElement);
            }
        }
    }
    /* Inserts a node after a reference node */
    insertAfter(newNode, referenceNode) {
        if (referenceNode.parentNode) {
            referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
        }
    }
    /* Finds the closest list element (UL or OL) to the given element */
    findClosestListElem(currentElement) {
        let closestListElement;
        while (!isNOU(currentElement)) {
            const hasUlParent = !isNOU(currentElement.closest('ul')) && currentElement.tagName !== 'UL';
            const hasOlParent = currentElement.tagName !== 'OL' && !isNOU(currentElement.closest('ol'));
            if (hasUlParent) {
                currentElement = currentElement.closest('ul');
            }
            else if (hasOlParent) {
                currentElement = currentElement.closest('ol');
            }
            else {
                currentElement = null;
            }
            closestListElement = !isNOU(currentElement) ? currentElement : closestListElement;
        }
        return closestListElement;
    }
    /* Adds 'msolistparagraph' class to elements that have MS Word list styles */
    addListClass(clipboardDataElement) {
        const allElements = clipboardDataElement.querySelectorAll('*');
        for (let i = 0; i < allElements.length; i++) {
            const currentElement = allElements[i];
            const elementStyle = currentElement.getAttribute('style');
            if (isNOU(elementStyle)) {
                continue;
            }
            // Remove all spaces and the first newline character from the elementStyle string
            const normalizedStyle = elementStyle.replace(/ /g, '').replace('\n', '');
            const hasMsoListStyle = normalizedStyle.indexOf('mso-list:l') >= 0;
            const hasNoMsoListClass = currentElement.className.toLowerCase().indexOf('msolistparagraph') === -1;
            const isNotHeading = currentElement.tagName.charAt(0) !== 'H';
            const isNotListElement = currentElement.tagName !== 'LI' &&
                currentElement.tagName !== 'OL' && currentElement.tagName !== 'UL';
            if (hasMsoListStyle && hasNoMsoListClass && isNotHeading && isNotListElement) {
                currentElement.classList.add('msolistparagraph');
            }
        }
    }
    /* Adds 'e-rte-table-border' class to tables that have border styles */
    addTableBorderClass(containerElement) {
        const tableElements = containerElement.querySelectorAll('table');
        let hasBorderStyle = false;
        for (let i = 0; i < tableElements.length; i++) {
            for (let j = 0; j < this.borderStyle.length; j++) {
                if (tableElements[i].innerHTML.indexOf(this.borderStyle[j]) >= 0) {
                    hasBorderStyle = true;
                    break;
                }
            }
            if (hasBorderStyle) {
                tableElements[i].classList.add('e-rte-table-border');
                hasBorderStyle = false; // Reset for the next table
            }
        }
    }
    /* Converts images from MS Word to appropriate formats */
    imageConversion(clipboardDataElement, rtfData) {
        this.checkVShape(clipboardDataElement);
        // First pass: Mark unsupported images and remove v:shapes attribute
        let imageElements = clipboardDataElement.querySelectorAll('img');
        this.markUnsupportedImages(imageElements);
        // Second pass: Process supported images
        imageElements = clipboardDataElement.querySelectorAll('img');
        if (imageElements.length === 0) {
            return;
        }
        const imageSources = [];
        const base64Sources = [];
        const imageNames = [];
        // Extract image sources and names
        this.extractImageInfo(imageElements, imageSources, imageNames);
        // Convert hex data to base64
        const hexValues = this.hexConversion(rtfData);
        this.processHexValues(hexValues, base64Sources);
        // Update image sources
        this.updateImageSources(clipboardDataElement, imageSources, base64Sources, imageNames);
        // Clean up unsupported images
        this.cleanUnsupportedImages(clipboardDataElement);
    }
    /* Marks unsupported images and removes v:shapes attribute */
    markUnsupportedImages(imageElements) {
        for (let i = 0; i < imageElements.length; i++) {
            const currentImage = imageElements[i];
            const shapesAttribute = currentImage.getAttribute('v:shapes');
            if (!isNOU(shapesAttribute)) {
                const isUnsupported = this.isUnsupportedImageShape(shapesAttribute);
                if (isUnsupported) {
                    currentImage.classList.add('e-rte-image-unsupported');
                }
                currentImage.removeAttribute('v:shapes');
            }
        }
    }
    /* Determines if an image shape is unsupported */
    isUnsupportedImageShape(shapesValue) {
        const supportedShapes = [
            'Picture', 'Chart', '圖片', 'Grafik', 'image', 'Graphic',
            '_x0000_s', '_x0000_i', 'img1', 'Immagine'
        ];
        for (let i = 0; i < supportedShapes.length; i++) {
            const shape = supportedShapes[i];
            if (shape === 'image') {
                if (shapesValue.toLowerCase().indexOf(shape) >= 0) {
                    return false;
                }
            }
            else if (shapesValue.indexOf(shape) >= 0) {
                return false;
            }
        }
        return true;
    }
    /* Extracts image information from image elements */
    extractImageInfo(imageElements, imageSources, imageNames) {
        for (let i = 0; i < imageElements.length; i++) {
            const currentImage = imageElements[i];
            if (!currentImage.classList.contains('e-rte-image-unsupported')) {
                const src = currentImage.getAttribute('src');
                imageSources.push(src);
                const srcParts = src.split('/');
                const lastPart = srcParts[srcParts.length - 1];
                const imageName = lastPart.split('.')[0] + i;
                imageNames.push(imageName);
            }
        }
    }
    /* Processes hex values and converts them to base64 */
    processHexValues(hexValues, base64Sources) {
        for (let i = 0; i < hexValues.length; i++) {
            const currentHex = hexValues[i];
            base64Sources.push({
                base64Data: !isNOU(currentHex.hex) ? this.convertToBase64(currentHex) : null,
                isCroppedImage: currentHex.isCroppedImage
            });
            if (currentHex.isCroppedImage) {
                this.cropImageDimensions.push({
                    goalWidth: currentHex.goalWidth,
                    goalHeight: currentHex.goalHeight,
                    cropLength: currentHex.cropLength,
                    cropTop: currentHex.cropTop,
                    cropR: currentHex.cropR,
                    cropB: currentHex.cropB
                });
            }
        }
    }
    /* Updates image sources with base64 data or marks as unsupported */
    updateImageSources(clipboardDataElement, imageSources, base64Sources, imageNames) {
        // 1. http://, https://
        // 2. www.
        // 3. blob:
        // 4. data:image/...;base64,...
        // eslint-disable-next-line
        const linkRegex = new RegExp(/([^\S]|^)(((https?\:\/\/)|(www\.)|(blob\:))|(data:image\/[a-zA-Z]+;base64,[\w+/=]+)(\S+))/gi);
        const imageElements = clipboardDataElement.querySelectorAll('img:not(.e-rte-image-unsupported)');
        for (let i = 0; i < imageElements.length; i++) {
            const currentImage = imageElements[i];
            const currentSource = imageSources[i];
            if (currentSource.match(linkRegex)) {
                currentImage.setAttribute('src', currentSource);
            }
            else {
                const currentBase64 = base64Sources[i];
                if (!isNOU(currentBase64) && !isNOU(currentBase64.base64Data)) {
                    currentImage.setAttribute('src', currentBase64.base64Data);
                }
                else {
                    currentImage.removeAttribute('src');
                    currentImage.classList.add('e-rte-image-unsupported');
                }
                if (!isNOU(currentBase64) && currentBase64.isCroppedImage) {
                    currentImage.classList.add('e-img-cropped');
                }
            }
            currentImage.setAttribute('id', 'msWordImg-' + imageNames[i]);
        }
    }
    /* Removes src attribute from unsupported images */
    cleanUnsupportedImages(clipboardDataElement) {
        const unsupportedImages = clipboardDataElement.querySelectorAll('.e-rte-image-unsupported');
        for (let i = 0; i < unsupportedImages.length; i++) {
            unsupportedImages[i].removeAttribute('src');
        }
    }
    /* Processes V:SHAPE elements and converts them to standard image elements */
    checkVShape(clipboardDataElement) {
        const allElements = clipboardDataElement.querySelectorAll('*');
        for (let i = 0; i < allElements.length; i++) {
            const currentElement = allElements[i];
            const elementNodeName = currentElement.nodeName;
            switch (elementNodeName) {
                case 'V:SHAPETYPE':
                    detach(currentElement);
                    break;
                case 'V:SHAPE':
                    this.processVShapeElement(currentElement);
                    break;
            }
        }
    }
    /* Processes a V:SHAPE element and converts it to a standard image if it contains image data */
    processVShapeElement(shapeElement) {
        const firstChild = shapeElement.firstElementChild;
        if (firstChild && firstChild.nodeName === 'V:IMAGEDATA') {
            const imageSrc = firstChild.getAttribute('src');
            const imageElement = createElement('img');
            imageElement.setAttribute('src', imageSrc);
            // Insert the new image before the V:SHAPE element
            shapeElement.parentElement.insertBefore(imageElement, shapeElement);
            // Remove the original V:SHAPE element
            detach(shapeElement);
        }
    }
    /* Converts hex value to base64 string */
    convertToBase64(hexValue) {
        const byteArr = this.conHexStringToBytes(hexValue.hex);
        const base64String = this.conBytesToBase64(byteArr);
        const mimeType = hexValue.type;
        const dataUri = mimeType ? 'data:' + mimeType + ';base64,' + base64String : null;
        return dataUri;
    }
    /* Converts byte array to base64 string */
    conBytesToBase64(byteArray) {
        let base64String = '';
        const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
        const byteArrayLength = byteArray.length;
        // Process bytes in groups of 3
        for (let i = 0; i < byteArrayLength; i += 3) {
            // Get a slice of 3 bytes (or fewer at the end)
            const threeBytes = byteArray.slice(i, i + 3);
            const threeBytesLength = threeBytes.length;
            const fourChars = [];
            // Pad the array if needed
            if (threeBytesLength < 3) {
                for (let j = threeBytesLength; j < 3; j++) {
                    threeBytes[j] = 0;
                }
            }
            // Convert 3 bytes (24 bits) into 4 base64 characters (6 bits each)
            fourChars[0] = (threeBytes[0] & 0xFC) >> 2;
            fourChars[1] = ((threeBytes[0] & 0x03) << 4) | (threeBytes[1] >> 4);
            fourChars[2] = ((threeBytes[1] & 0x0F) << 2) | ((threeBytes[2] & 0xC0) >> 6);
            fourChars[3] = threeBytes[2] & 0x3F;
            // Convert indices to base64 characters
            for (let j = 0; j < 4; j++) {
                // Add padding '=' for incomplete byte groups
                if (j <= threeBytesLength) {
                    base64String += base64Chars.charAt(fourChars[j]);
                }
                else {
                    base64String += '=';
                }
            }
        }
        return base64String;
    }
    /* Converts a hexadecimal string to an array of bytes */
    conHexStringToBytes(hexString) {
        const byteArray = [];
        const byteCount = hexString.length / 2;
        for (let i = 0; i < byteCount; i++) {
            const hexByte = hexString.substr(i * 2, 2);
            const byte = parseInt(hexByte, 16);
            byteArray.push(byte);
        }
        return byteArray;
    }
    /* Converts RTF data to hex values for image processing */
    hexConversion(rtfData) {
        const regExp = RegExp;
        const pictureHeaderPattern = new regExp('\\{\\\\pict[\\s\\S]+?\\\\bliptag-?\\d+(\\\\blipupi-?\\d+)?(\\{\\\\\\*\\\\blipuid\\s?[\\da-fA-F]+)?[\\s\\}]*?');
        const picturePattern = new regExp('(?:(' + pictureHeaderPattern.source + '))([\\da-fA-F\\s]+)\\}', 'g');
        const matchedImages = rtfData.match(picturePattern);
        const result = [];
        if (isNOU(matchedImages)) {
            return result;
        }
        for (let i = 0; i < matchedImages.length; i++) {
            const currentImage = matchedImages[i];
            // Skip bullet images
            if (currentImage.indexOf('fIsBullet') !== -1 && currentImage.indexOf('wzName') === -1) {
                continue;
            }
            if (!pictureHeaderPattern.test(currentImage)) {
                continue;
            }
            const imageData = this.extractImageData(currentImage, pictureHeaderPattern);
            if (imageData) {
                result.push(imageData);
            }
        }
        return result;
    }
    /* Extracts image data from RTF picture content */
    extractImageData(imageContent, pictureHeaderPattern) {
        let imageType = null;
        // Determine image type
        if (imageContent.indexOf('\\pngblip') !== -1) {
            imageType = 'image/png';
        }
        else if (imageContent.indexOf('\\jpegblip') !== -1) {
            imageType = 'image/jpeg';
        }
        else if (imageContent.indexOf('\\emfblip') !== -1) {
            imageType = null;
        }
        else {
            return null;
        }
        // Check if image is cropped
        const isCroppedImage = this.isImageCropped(imageContent);
        const cropData = {
            goalWidth: 0,
            goalHeight: 0,
            cropLength: 0,
            cropTop: 0,
            cropR: 0,
            cropB: 0
        };
        if (isCroppedImage) {
            cropData.goalWidth = this.extractCropValue('wgoal', imageContent);
            cropData.goalHeight = this.extractCropValue('hgoal', imageContent);
            cropData.cropLength = this.extractCropValue('cropl', imageContent);
            cropData.cropTop = this.extractCropValue('cropt', imageContent);
            cropData.cropR = this.extractCropValue('cropr', imageContent);
            cropData.cropB = this.extractCropValue('cropb', imageContent);
        }
        return {
            hex: imageType ? imageContent.replace(pictureHeaderPattern, '').replace(/[^\da-fA-F]/g, '') : null,
            type: imageType,
            isCroppedImage: isCroppedImage,
            goalWidth: cropData.goalWidth,
            goalHeight: cropData.goalHeight,
            cropLength: cropData.cropLength,
            cropTop: cropData.cropTop,
            cropR: cropData.cropR,
            cropB: cropData.cropB
        };
    }
    /* Determines if an image is cropped based on crop values */
    isImageCropped(rtfData) {
        const hasLeftTopCrop = this.extractCropValue('cropl', rtfData) > 0 &&
            this.extractCropValue('cropt', rtfData) > 0;
        const hasRightCrop = this.extractCropValue('cropr', rtfData) > 0;
        const hasBottomCrop = this.extractCropValue('cropb', rtfData) > 0;
        return hasLeftTopCrop || hasRightCrop || hasBottomCrop;
    }
    /* Extracts crop value from RTF data for a specific crop property */
    extractCropValue(cropProperty, rtfData) {
        // Normalize RTF data by handling line breaks
        const normalizedRtfData = rtfData
            .replace(/\r\n\\/g, '\\')
            .replace(/\n/g, '\\');
        const regExp = RegExp;
        const cropPattern = new regExp('\\\\pic' + cropProperty + '(\\-?\\d+)\\\\');
        // Execute the pattern against the normalized RTF data
        const matchResult = cropPattern.exec(normalizedRtfData);
        // Return 0 if no match found or match doesn't have the expected format
        if (!matchResult || matchResult.length < 2) {
            return 0;
        }
        return parseInt(matchResult[1], 10);
    }
    /* Removes class attributes from elements except for specific classes */
    removeClassName(clipboardDataElement) {
        const elementsWithClass = clipboardDataElement.querySelectorAll('*[class]:not(.e-img-cropped):not(.e-rte-image-unsupported)');
        for (let i = 0; i < elementsWithClass.length; i++) {
            elementsWithClass[i].removeAttribute('class');
        }
    }
    /* Adds line breaks in place of empty elements with &nbsp; */
    breakLineAddition(clipboardDataElement) {
        const allElements = clipboardDataElement.querySelectorAll('*');
        for (let i = 0; i < allElements.length; i++) {
            const currentElement = allElements[i];
            if (this.isReplacableWithBreak(currentElement)) {
                const detachableElement = this.findDetachElem(currentElement);
                const brElement = createElement('br');
                const hasNbsp = this.hasNonBreakingSpace(detachableElement);
                if (!hasNbsp && !isNOU(detachableElement.parentElement)) {
                    detachableElement.parentElement.insertBefore(brElement, detachableElement);
                    detach(detachableElement);
                }
            }
        }
    }
    /* Determines if an element should be replaced with a line break */
    isReplacableWithBreak(element) {
        const hasNoChildren = element.children.length === 0;
        const hasNbspContent = element.innerHTML === '&nbsp;';
        const isNotInListItem = !element.closest('li');
        const isNotInTableCell = !element.closest('td');
        const isNotSpan = element.nodeName !== 'SPAN';
        const isIsolatedSpan = element.nodeName === 'SPAN' &&
            isNOU(element.previousElementSibling) && isNOU(element.nextElementSibling);
        return hasNoChildren && hasNbspContent && isNotInListItem &&
            isNotInTableCell && (isNotSpan || isIsolatedSpan);
    }
    /* Checks if an element contains non-breaking space characters */
    hasNonBreakingSpace(element) {
        const hasText = element.textContent.length > 0;
        const nbspMatches = element.textContent.match(/\u00a0/g);
        const hasNbspMatches = nbspMatches !== null && nbspMatches.length > 0;
        return hasText && hasNbspMatches;
    }
    /* Finds the topmost empty parent element that should be removed */
    findDetachElem(element) {
        const parent = element.parentElement;
        if (isNOU(parent)) {
            return element;
        }
        const isEmptyParent = parent.textContent.trim() === '';
        const isNotTableCell = parent.tagName !== 'TD' && parent.tagName !== 'TH';
        const hasNoImages = isNOU(parent.querySelector('img'));
        if (isEmptyParent && isNotTableCell && hasNoImages) {
            return this.findDetachElem(parent);
        }
        return element;
    }
    /* Removes unwanted elements from the HTML content */
    removeUnwantedElements(clipboardDataElement) {
        // Remove style elements
        this.removeStyleElements(clipboardDataElement);
        // Remove elements by tag name using regex
        this.removeElementsByTagName(clipboardDataElement);
    }
    /* Removes style elements from the container */
    removeStyleElements(clipboardDataElement) {
        const styleElement = clipboardDataElement.querySelector('style');
        if (!isNOU(styleElement)) {
            detach(styleElement);
        }
    }
    /* Removes elements by tag name using regex */
    removeElementsByTagName(clipboardDataElement) {
        let htmlContent = clipboardDataElement.innerHTML;
        const regExpConstructor = RegExp;
        for (let i = 0; i < this.removableElements.length; i++) {
            const tagName = this.removableElements[i];
            const startTagPattern = new regExpConstructor('<' + tagName + '\\s*[^>]*>', 'g');
            const endTagPattern = new regExpConstructor('</' + tagName + '>', 'g');
            htmlContent = htmlContent.replace(startTagPattern, '');
            htmlContent = htmlContent.replace(endTagPattern, '');
        }
        clipboardDataElement.innerHTML = htmlContent;
        clipboardDataElement.querySelectorAll(':empty');
    }
    /* Finds the topmost empty parent element that should be removed */
    findDetachEmptyElem(element) {
        if (isNOU(element.parentElement)) {
            return null;
        }
        const parentElement = element.parentElement;
        // Check if parent has non-breaking spaces
        const hasNbsp = this.hasNonBreakingSpace(parentElement);
        // Check if parent is empty and not a special element
        const isEmptyParent = !hasNbsp && parentElement.textContent.trim() === '';
        const isNotMsWordContent = parentElement.getAttribute('id') !== 'MSWord-Content';
        const isNotMsoListParagraph = !this.hasParentWithClass(element, 'MsoListParagraph');
        const hasNoImages = isNOU(parentElement.querySelector('img'));
        if (isEmptyParent && isNotMsWordContent && isNotMsoListParagraph && hasNoImages) {
            return this.findDetachEmptyElem(parentElement);
        }
        return element;
    }
    /* Checks if an element has a parent with the specified class */
    hasParentWithClass(element, className) {
        let currentParentElem = element.parentElement;
        while (!isNOU(currentParentElem)) {
            if (currentParentElem.classList.contains(className)) {
                return true;
            }
            currentParentElem = currentParentElem.parentElement;
        }
        return false;
    }
    /* Removes empty elements from the HTML content */
    removeEmptyElements(containerElement) {
        const emptyElements = containerElement.querySelectorAll(':empty');
        for (let i = 0; i < emptyElements.length; i++) {
            const currentElement = emptyElements[i];
            // Handle empty cells with MsoNormal class
            if (this.isEmptyCellWithMsoNormal(currentElement)) {
                currentElement.innerHTML = '-';
            }
            // Check if div has border
            const isDivWithoutBorder = this.isDivWithoutBorder(currentElement);
            // Skip certain elements that should remain empty
            if (this.shouldRemoveEmptyElement(currentElement, isDivWithoutBorder)) {
                const detachableElement = this.findDetachEmptyElem(currentElement);
                if (!isNOU(detachableElement)) {
                    detach(detachableElement);
                }
            }
        }
    }
    /* Checks if an element is an empty cell with MsoNormal class */
    isEmptyCellWithMsoNormal(element) {
        const parentCell = element.closest('td');
        return !isNOU(parentCell) && !isNOU(parentCell.querySelector('.MsoNormal'));
    }
    /* Checks if a div element has no border */
    isDivWithoutBorder(element) {
        if (element.tagName !== 'DIV') {
            return true;
        }
        const borderBottom = element.style.borderBottom;
        return borderBottom === 'none' || borderBottom === '';
    }
    /* Determines if an empty element should be removed */
    shouldRemoveEmptyElement(element, isDivWithoutBorder) {
        const preservedTags = ['IMG', 'BR', 'IFRAME', 'TD', 'TH', 'HR'];
        return preservedTags.indexOf(element.tagName) === -1 && isDivWithoutBorder;
    }
    /* Removes empty meta tags from the HTML content */
    removeEmptyMetaTags(clipboardDataElement) {
        const emptyMetaTags = clipboardDataElement.querySelectorAll('meta:empty');
        // Process in reverse order to avoid index issues when removing elements
        for (let i = emptyMetaTags.length - 1; i >= 0; i--) {
            const metaTag = emptyMetaTags[i];
            if (metaTag.textContent === '') {
                detach(metaTag);
            }
        }
    }
    /* Corrects styles in the HTML content based on Word paste style configuration */
    styleCorrection(clipboardDataElement, allowedStyleProperties) {
        const styleElements = clipboardDataElement.querySelectorAll('style');
        let styleRules = [];
        if (styleElements.length === 0) {
            return;
        }
        // Extract style rules from the first or second style element
        const styleRulePattern = /[\S ]+\s+{[\s\S]+?}/gi;
        if (!isNOU(styleElements[0].innerHTML.match(styleRulePattern))) {
            styleRules = styleElements[0].innerHTML.match(styleRulePattern);
        }
        else if (styleElements.length > 1) {
            styleRules = styleElements[1].innerHTML.match(styleRulePattern);
        }
        // Convert style rules to a structured object
        const styleClassObject = !isNOU(styleRules) ? this.findStyleObject(styleRules) : null;
        if (isNOU(styleClassObject)) {
            return;
        }
        // Process style rules
        const selectors = this.sortSelectors(Object.keys(styleClassObject));
        let styleValues = selectors.map((selector) => {
            return styleClassObject[`${selector}`];
        });
        // Remove unwanted styles and filter existing styles
        styleValues = this.removeUnwantedStyle(styleValues, allowedStyleProperties);
        this.filterStyles(clipboardDataElement, allowedStyleProperties);
        // Apply styles to matching elements
        this.applyStylesToElements(clipboardDataElement, selectors, styleValues);
        // Process list-specific styles
        this.processListStyles(clipboardDataElement, selectors, styleValues);
        // Remove table width styles set to 0
        this.removeTableWidthZero(clipboardDataElement);
    }
    /* Removes width style from table elements where width is set to 0 */
    removeTableWidthZero(clipboardDataElement) {
        const tableElements = clipboardDataElement.querySelectorAll('table');
        for (let i = 0; i < tableElements.length; i++) {
            const tableElement = tableElements[i];
            const styleAttribute = tableElement.getAttribute('style');
            if (!isNOU(styleAttribute) && styleAttribute.indexOf('width') !== -1) {
                const widthMatch = styleAttribute.match(/width\s*:\s*0(?:px)?\s*;?/);
                if (!isNOU(widthMatch)) {
                    const newStyle = styleAttribute.replace(widthMatch[0], '').replace(/;\s*$/, '');
                    if (newStyle.trim() === '') {
                        tableElement.removeAttribute('style');
                    }
                    else {
                        tableElement.setAttribute('style', newStyle);
                    }
                }
            }
        }
    }
    // Sorts CSS selectors to ensure a specific application order: classes and then other elements.
    sortSelectors(selectors) {
        return selectors.sort((a, b) => (a.trim().startsWith('.') ? 0 : 1) - (b.trim().startsWith('.') ? 0 : 1));
    }
    /* Filters inline styles to keep only allowed style properties */
    filterStyles(clipboardDataElement, allowedStyleProperties) {
        const elementsWithStyle = clipboardDataElement.querySelectorAll('*[style]');
        for (let i = 0; i < elementsWithStyle.length; i++) {
            const currentElement = elementsWithStyle[i];
            const styleDeclarations = currentElement.getAttribute('style').split(';');
            let filteredStyle = '';
            // Process each style declaration
            for (let j = 0; j < styleDeclarations.length; j++) {
                const declaration = styleDeclarations[j];
                const propertyName = declaration.split(':')[0].trim();
                // Keep only allowed style properties
                if (allowedStyleProperties.indexOf(propertyName) >= 0) {
                    filteredStyle += declaration + ';';
                }
            }
            // Apply filtered styles back to the element
            currentElement.setAttribute('style', filteredStyle);
        }
    }
    /* Applies styles to elements matching the selectors */
    applyStylesToElements(clipboardDataElement, selectors, styleValues) {
        let matchedElements;
        let isClassSelector = false;
        const specialSelectorPattern = /^(p|div|li)\.(1|10|11)$/;
        for (let i = 0; i < selectors.length; i++) {
            const currentSelector = selectors[i];
            const selectorParts = currentSelector.split('.');
            const baseSelector = selectorParts[0];
            // Determine how to select elements based on the selector format
            if (baseSelector === '') {
                // Class selector (className)
                const className = selectorParts[1];
                matchedElements = clipboardDataElement.getElementsByClassName(className);
                isClassSelector = true;
            }
            else if ((selectorParts.length === 1 && baseSelector.indexOf('@') >= 0) ||
                (specialSelectorPattern.test(currentSelector))) {
                // Skip special selectors
                continue;
            }
            else if (selectorParts.length === 1 && baseSelector.indexOf('@') < 0) {
                // Tag selector (tagName)
                matchedElements = clipboardDataElement.getElementsByTagName(baseSelector);
            }
            else {
                // Complex selector (tag.class, etc.)
                matchedElements = clipboardDataElement.querySelectorAll(currentSelector);
            }
            // Apply styles to each matching element
            this.applyStyleToElementCollection(matchedElements, currentSelector, styleValues[i], isClassSelector);
            isClassSelector = false;
        }
    }
    /* Applies styles to a collection of elements */
    applyStyleToElementCollection(elements, selector, styleValue, isClassSelector) {
        for (let j = 0; j < elements.length; j++) {
            const currentElement = elements[j];
            // Skip paragraph elements inside list items
            if (currentElement.closest('li') && selector === 'p') {
                continue;
            }
            const existingStyle = currentElement.getAttribute('style');
            const hasExistingStyle = !isNOU(existingStyle) && existingStyle.trim() !== '';
            if (hasExistingStyle) {
                // Process existing style
                const styleDeclarations = styleValue.split(';');
                this.removeBorderNoneStyles(styleDeclarations);
                if (!isClassSelector) {
                    this.removeOverlappingStyles(styleDeclarations, existingStyle);
                }
                const combinedStyle = styleDeclarations.join(';') + ';' + existingStyle;
                currentElement.setAttribute('style', combinedStyle);
            }
            else {
                // Apply clean style
                styleValue = styleValue
                    .replace(/text-indent:-.*?;?/g, '') // Remove 'text-indent'
                    .replace(/border:\s*none;?/g, '') // Remove 'border:none'
                    .trim();
                currentElement.setAttribute('style', styleValue);
            }
        }
    }
    /* Removes 'border: none' styles from the style array */
    removeBorderNoneStyles(styleDeclarations) {
        for (let i = 0; i < styleDeclarations.length; i++) {
            const declarationParts = styleDeclarations[i].split(':');
            if (declarationParts[0] === 'border' && declarationParts[1] === 'none') {
                styleDeclarations.splice(i, 1);
                i--;
            }
        }
    }
    /* Removes styles that would overlap with existing inline styles */
    removeOverlappingStyles(styleDeclarations, existingStyle) {
        for (let i = 0; i < styleDeclarations.length; i++) {
            const propertyName = styleDeclarations[i].split(':')[0];
            if (existingStyle.indexOf(propertyName + ':') >= 0) {
                styleDeclarations.splice(i, 1);
                i--;
            }
        }
    }
    /* Processes list-specific styles */
    processListStyles(containerElement, selectors, styleValues) {
        const listClasses = [
            'MsoListParagraphCxSpFirst',
            'MsoListParagraphCxSpMiddle',
            'MsoListParagraphCxSpLast'
        ];
        for (let i = 0; i < listClasses.length; i++) {
            const listClassName = listClasses[i];
            const listSelector = 'li.' + listClassName;
            const selectorIndex = selectors.indexOf(listSelector);
            if (selectorIndex > -1) {
                const listElements = containerElement.querySelectorAll('ol.' + listClassName + ', ul.' + listClassName);
                this.adjustListMargins(listElements, styleValues[selectorIndex]);
            }
        }
    }
    /* Adjusts margins for list elements */
    adjustListMargins(listElements, styleValue) {
        for (let j = 0; j < listElements.length; j++) {
            const listElement = listElements[j];
            const existingStyle = listElement.getAttribute('style');
            const hasValidStyle = !isNOU(existingStyle) &&
                existingStyle.trim() !== '' && listElement.style.marginLeft !== '';
            if (hasValidStyle) {
                const styleDeclarations = styleValue.split(';');
                for (let k = 0; k < styleDeclarations.length; k++) {
                    const declaration = styleDeclarations[k];
                    const propertyName = declaration.split(':')[0];
                    if ('margin-left'.indexOf(propertyName) >= 0) {
                        this.adjustMarginLeftValue(listElement, declaration);
                    }
                }
            }
        }
    }
    /* Adjusts the margin-left value for a list element */
    adjustMarginLeftValue(element, marginDeclaration) {
        const declarationParts = marginDeclaration.split(':');
        const marginValue = declarationParts[1];
        const elementMargin = element.style.marginLeft;
        const hasInchUnits = !isNOU(marginValue) &&
            marginValue.indexOf('in') >= 0 && elementMargin.indexOf('in') >= 0;
        if (hasInchUnits) {
            const classStyleValue = parseFloat(marginValue.split('in')[0]);
            const inlineStyleValue = parseFloat(elementMargin.split('in')[0]);
            element.style.marginLeft = (inlineStyleValue - classStyleValue) + 'in';
        }
    }
    /* Filters style values to keep only allowed style properties */
    removeUnwantedStyle(styleValues, allowedStyleProperties) {
        const filteredValues = [];
        for (let i = 0; i < styleValues.length; i++) {
            const styleDeclarations = styleValues[i].split(';');
            let filteredDeclarations = '';
            for (let j = 0; j < styleDeclarations.length; j++) {
                const declaration = styleDeclarations[j];
                const propertyName = declaration.split(':')[0];
                // Keep only allowed style properties
                if (allowedStyleProperties.indexOf(propertyName) >= 0) {
                    filteredDeclarations += declaration + ';';
                }
            }
            filteredValues[i] = filteredDeclarations;
        }
        return filteredValues;
    }
    /* Converts CSS rule strings into a structured object mapping selectors to style declarations */
    findStyleObject(styleRules) {
        const styleClassObject = {};
        for (let i = 0; i < styleRules.length; i++) {
            const currentRule = styleRules[i];
            // Extract selector and style parts from the rule
            let selectorText = currentRule.replace(/([\S ]+\s+){[\s\S]+?}/gi, '$1');
            let styleText = currentRule.replace(/[\S ]+\s+{([\s\S]+?)}/gi, '$1');
            // Clean up whitespace and line breaks
            selectorText = this.cleanupStyleText(selectorText);
            styleText = this.cleanupStyleText(styleText);
            // Map each selector to the style declarations
            const selectors = selectorText.split(', ');
            for (let j = 0; j < selectors.length; j++) {
                styleClassObject[selectors[j]] = styleText;
            }
        }
        return styleClassObject;
    }
    /* Cleans up style text by removing whitespace and line breaks */
    cleanupStyleText(text) {
        let cleanedText = text;
        // Remove leading and trailing whitespace
        cleanedText = cleanedText.replace(/^[\s]|[\s]$/gm, '');
        // Remove line breaks
        cleanedText = cleanedText.replace(/\n|\r|\n\r/g, '');
        return cleanedText;
    }
    /* Removes HTML comments from an element */
    removingComments(clipboardDataElement) {
        const htmlContent = clipboardDataElement.innerHTML;
        const commentPattern = /<!--[\s\S]*?-->/g;
        const cleanedContent = htmlContent.replace(commentPattern, '');
        clipboardDataElement.innerHTML = cleanedContent;
    }
    /* Cleans up HTML content and identifies list nodes for conversion */
    listCleanUp(containerElement, listNodes) {
        const nodesToRemove = [];
        let previousWasMsoList = false;
        const allElements = containerElement.querySelectorAll('*');
        for (let i = 0; i < allElements.length; i++) {
            const currentElement = allElements[i];
            // Check if element should be ignored
            if (this.shouldIgnoreElement(currentElement)) {
                nodesToRemove.push(currentElement);
                continue;
            }
            // Check if element is an MS Word list paragraph
            if (this.isMsoListParagraph(currentElement)) {
                // Add a null separator for new list if needed
                if (this.isFirstListItem(currentElement) && listNodes.length > 0 &&
                    listNodes[listNodes.length - 1] !== null) {
                    listNodes.push(null);
                }
                // Add the list node
                listNodes.push(currentElement);
            }
            // Add a null separator when transitioning from list to non-list block
            if (this.shouldAddListSeparator(previousWasMsoList, currentElement)) {
                listNodes.push(null);
            }
            // Update previous state flag for next iteration
            if (this.isBlockElement(currentElement)) {
                previousWasMsoList = this.isMsoListParagraph(currentElement);
            }
        }
        // Add a final null separator if needed
        if (listNodes.length > 0 && listNodes[listNodes.length - 1] !== null) {
            listNodes.push(null);
        }
        return listNodes;
    }
    /* Determines if an element should be ignored during cleanup */
    shouldIgnoreElement(element) {
        const isNotInIgnorableList = this.ignorableNodes.indexOf(element.nodeName) === -1;
        const isEmptyTextNode = element.nodeType === 3 && element.textContent.trim() === '';
        return isNotInIgnorableList || isEmptyTextNode;
    }
    /* Determines if an element is an MS Word list paragraph */
    isMsoListParagraph(element) {
        const elementClass = element.className;
        const hasClassName = elementClass && elementClass.toLowerCase().indexOf('msolistparagraph') !== -1;
        const elementStyles = element.getAttribute('style');
        const hasMsoListStyle = !isNOU(elementStyles) && elementStyles.indexOf('mso-list:') >= 0;
        return hasClassName && hasMsoListStyle;
    }
    /* Determines if an element is the first item in a list */
    isFirstListItem(element) {
        return element.className.indexOf('MsoListParagraphCxSpFirst') >= 0;
    }
    /* Determines if a list separator should be added */
    shouldAddListSeparator(previousWasMsoList, currentElement) {
        return previousWasMsoList &&
            this.isBlockElement(currentElement) && !this.isMsoListParagraph(currentElement);
    }
    /* Determines if an element is a block element */
    isBlockElement(element) {
        return this.blockNode.indexOf(element.nodeName.toLowerCase()) !== -1;
    }
    /**
     * Converts MS Word list nodes to standard HTML lists
     *
     * @param {Element[]} listNodes - Array of list nodes to convert
     * @returns {void} - No return value
     * @private
     */
    listConverter(listNodes) {
        const convertedLists = [];
        const listCollection = [];
        const currentListStyle = '';
        // Process list nodes and build collection
        this.processListNodes(listNodes, convertedLists, listCollection, currentListStyle);
        // Replace original nodes with converted lists
        this.replaceNodesWithLists(listNodes, convertedLists);
    }
    /* Processes list nodes and builds collection of list data */
    processListNodes(listNodes, convertedLists, listCollection, currentListStyle) {
        let listFormatOverride;
        for (let i = 0; i < listNodes.length; i++) {
            const currentNode = listNodes[i];
            // Handle null separator - convert collected items to list
            if (currentNode === null) {
                convertedLists.push({
                    content: this.makeConversion(listCollection),
                    node: listNodes[i - 1]
                });
                listCollection = [];
                continue;
            }
            // Fix outline level in style
            this.fixOutlineLevel(currentNode);
            // Extract list properties
            const nodeStyle = currentNode.getAttribute('style') || '';
            const nestingLevel = this.extractNestingLevel(nodeStyle);
            listFormatOverride = this.extractListFormatOverride(nodeStyle, listFormatOverride);
            // Process list content
            this.listContents = [];
            this.getListContent(currentNode);
            // Skip if no list content
            if (isNOU(this.listContents[0])) {
                continue;
            }
            // Determine list properties
            const listProperties = this.determineListProperties(this.listContents[0], i, listNodes, currentNode);
            // Collect content items
            const contentItems = [];
            for (let j = 1; j < this.listContents.length; j++) {
                contentItems.push(this.listContents[j]);
            }
            // Get class name and update style
            const className = !isNOU(currentNode.className) ? currentNode.className : '';
            currentListStyle = this.updateNodeStyle(currentNode, nodeStyle);
            // Add to collection
            listCollection.push({
                listType: listProperties.type,
                content: contentItems,
                nestedLevel: nestingLevel,
                listFormatOverride: listFormatOverride,
                class: className,
                listStyle: currentListStyle,
                listStyleTypeName: listProperties.styleType,
                start: listProperties.startAttr,
                styleMarginLeft: listProperties.marginLeft
            });
        }
    }
    /* Fixes outline level in style attribute */
    fixOutlineLevel(node) {
        const style = node.getAttribute('style');
        if (style && style.indexOf('mso-outline-level') !== -1) {
            node.setAttribute('style', style.replace('mso-outline-level', 'mso-outline'));
        }
    }
    /* Extracts nesting level from style */
    extractNestingLevel(style) {
        if (style && style.indexOf('level') !== -1) {
            // eslint-disable-next-line
            return parseInt(style.charAt(style.indexOf('level') + 5), null);
        }
        return 1;
    }
    /* Extracts list format override from style */
    extractListFormatOverride(style, listFormatOverride) {
        if (style && style.indexOf('mso-list:') !== -1) {
            if (style.match(/mso-list:[^;]+;?/)) {
                const normalizedStyle = style.replace(new RegExp('\\n', 'g'), '').split(' ').join('');
                const msoListValue = normalizedStyle.match(/mso-list:[^;]+;?/)[0].split(':l');
                return isNOU(msoListValue) ? null : parseInt(msoListValue[1].split('level')[0], 10);
            }
            else {
                return null;
            }
        }
        return listFormatOverride;
    }
    /* Determines list properties based on content */
    determineListProperties(listContent, index, listNodes, currentNode) {
        const result = {
            type: listContent.trim().length > 1 ? 'ol' : 'ul',
            styleType: ''
        };
        // Determine list style type
        result.styleType = this.getlistStyleType(listContent, result.type);
        // Determine start attribute for ordered lists
        if (result.type === 'ol' && (index === 0 || listNodes[index - 1] === null)) {
            result.startAttr = this.determineStartAttribute(listContent, result.styleType);
        }
        // Get margin-left if present
        if (currentNode.style.marginLeft !== '') {
            result.marginLeft = currentNode.style.marginLeft;
        }
        return result;
    }
    /* Determines start attribute for ordered lists */
    determineStartAttribute(listContent, listStyleType) {
        const startString = listContent.split('.')[0];
        const standardListTypes = ['A', 'a', 'I', 'i', 'α', '1', '01', '1-']; // Add '1-' for rare list type
        if (standardListTypes.indexOf(startString) !== -1) {
            return undefined;
        }
        switch (listStyleType) {
            case 'decimal':
            case 'decimal-leading-zero':
                if (!isNaN(parseInt(startString, 10))) {
                    return parseInt(startString, 10);
                }
                break;
            case 'upper-alpha':
                return startString.split('.')[0].charCodeAt(0) - 64;
            case 'lower-alpha':
                return startString.split('.')[0].charCodeAt(0) - 96;
            case 'upper-roman':
                return this.upperRomanNumber.indexOf(startString.split('.')[0]) + 1;
            case 'lower-roman':
                return this.lowerRomanNumber.indexOf(startString.split('.')[0]) + 1;
            case 'lower-greek':
                return this.lowerGreekNumber.indexOf(startString.split('.')[0]) + 1;
            default:
                return undefined;
        }
        return undefined;
    }
    /* Updates node style */
    updateNodeStyle(node, style) {
        if (!isNOU(node.getAttribute('style'))) {
            node.setAttribute('style', style.replace('text-align:start;', ''));
            node.style.textIndent = '';
            return node.getAttribute('style');
        }
        return '';
    }
    /* Replaces original nodes with converted lists */
    replaceNodesWithLists(listNodes, convertedLists) {
        let currentNode = listNodes.shift();
        while (currentNode) {
            const elementsToInsert = [];
            // Find matching converted list
            for (let i = 0; i < convertedLists.length; i++) {
                if (convertedLists[i].node === currentNode) {
                    const convertedContent = convertedLists[i].content;
                    // Collect all child nodes
                    for (let j = 0; j < convertedContent.childNodes.length; j++) {
                        elementsToInsert.push(convertedContent.childNodes[j]);
                    }
                    // Insert before the original node
                    for (let j = 0; j < elementsToInsert.length; j++) {
                        currentNode.parentElement.insertBefore(elementsToInsert[j], currentNode);
                    }
                    break;
                }
            }
            // Remove the original node
            currentNode.remove();
            // Get next node
            currentNode = listNodes.shift();
            if (!currentNode) {
                currentNode = listNodes.shift();
            }
        }
    }
    /* Determines the CSS list-style-type based on list content and type */
    getlistStyleType(listContent, listType) {
        // Extract the marker text before any period
        const markerText = listContent.split('.')[0];
        if (listType === 'ol') {
            return this.getOrderedListStyleType(markerText);
        }
        else {
            return this.getUnorderedListStyleType(markerText);
        }
    }
    /* Determines the CSS list-style-type for ordered lists */
    getOrderedListStyleType(markerText) {
        const charCode = markerText.charCodeAt(0);
        // Check for Roman numerals
        if (this.upperRomanNumber.indexOf(markerText) > -1) {
            return 'upper-roman';
        }
        if (this.lowerRomanNumber.indexOf(markerText) > -1) {
            return 'lower-roman';
        }
        // Check for Greek letters
        if (this.lowerGreekNumber.indexOf(markerText) > -1) {
            return 'lower-greek';
        }
        // Check for uppercase letters (A-Z)
        if (charCode > 64 && charCode < 91) {
            return 'upper-alpha';
        }
        // Check for lowercase letters (a-z)
        if (charCode > 96 && charCode < 123) {
            return 'lower-alpha';
        }
        // Check for leading zero numbers (01, 02, etc.)
        const isLeadingZeroNumber = markerText.length > 1 &&
            markerText[0] === '0' && !isNaN(Number(markerText));
        if (isLeadingZeroNumber) {
            return 'decimal-leading-zero';
        }
        // Default to decimal
        return 'decimal';
    }
    /* Determines the CSS list-style-type for unordered lists */
    getUnorderedListStyleType(markerText) {
        switch (markerText) {
            case 'o':
                return 'circle';
            case '§':
                return 'square';
            default:
                return 'disc';
        }
    }
    /* Converts a collection of MSWord list items into HTML list elements */
    makeConversion(collection) {
        const rootElement = createElement('div');
        const CURRENT_ITEM_CLASS = 'e-current-list-item';
        if (collection.length === 0) {
            return rootElement;
        }
        let currentListElement;
        let currentNestingLevel = 1;
        let currentListItem;
        let listItemCount = 0;
        let currentFormatOverride = collection[0].listFormatOverride;
        for (let i = 0; i < collection.length; i++) {
            const currentItem = collection[i];
            const isStandardList = this.isStandardListType(currentItem.class);
            // Remove tracking class from previous item
            if (currentListItem) {
                currentListItem.classList.remove(CURRENT_ITEM_CLASS);
            }
            // Reset previous list item if list type changes
            if (this.shouldResetListItem(currentListItem, i, collection, isStandardList)) {
                currentListItem = null;
            }
            // Create paragraph element with content
            const paragraphElement = this.createParagraphWithContent(currentItem);
            // Handle different nesting scenarios
            if (this.isNewRootList(currentItem, listItemCount, currentFormatOverride)) {
                // Create new root list
                currentListElement = this.createRootList(rootElement, currentItem, paragraphElement);
                currentListItem = currentListElement.querySelector('.' + CURRENT_ITEM_CLASS);
            }
            else if (this.isSameLevelList(currentItem, currentNestingLevel, currentFormatOverride)) {
                // Add item to same level list
                currentListElement = this.addToSameLevelList(currentItem, currentListElement, paragraphElement, currentListItem, rootElement);
                currentListItem = currentListElement.querySelector('.' + CURRENT_ITEM_CLASS);
            }
            else if (this.isDeeperNestedList(currentItem, currentNestingLevel)) {
                // Create deeper nested list
                currentListElement = this.createNestedList(currentItem, currentListItem, paragraphElement, isStandardList, rootElement, currentNestingLevel);
                currentListItem = currentListElement.querySelector('.' + CURRENT_ITEM_CLASS);
            }
            else if (this.isTopLevelList(currentItem)) {
                // Create or use existing top-level list
                currentListElement = this.handleTopLevelList(currentItem, rootElement, paragraphElement);
                currentListItem = currentListElement.querySelector('.' + CURRENT_ITEM_CLASS);
            }
            else {
                // Handle other nesting scenarios
                this.handleOtherNestingScenarios(currentItem, currentListItem, paragraphElement, currentFormatOverride);
                currentListItem = rootElement.querySelector('.' + CURRENT_ITEM_CLASS);
            }
            // Apply styles and attributes to list item
            this.applyListItemStyles(currentListItem, currentItem);
            // Update state for next iteration
            currentNestingLevel = currentItem.nestedLevel;
            currentFormatOverride = currentItem.listFormatOverride;
            listItemCount++;
            // Set start attribute if needed
            this.setStartAttributeIfNeeded(currentListElement, currentItem);
        }
        // Clean up - remove tracking class from any remaining elements
        const trackedItems = rootElement.querySelectorAll('.' + CURRENT_ITEM_CLASS);
        for (let i = 0; i < trackedItems.length; i++) {
            trackedItems[i].classList.remove(CURRENT_ITEM_CLASS);
            if (trackedItems[i].className === '') {
                trackedItems[i].removeAttribute('class');
            }
        }
        return rootElement;
    }
    /* Checks if the list item is a standard list type */
    isStandardListType(className) {
        const standardListClasses = [
            'MsoListParagraphCxSpFirst',
            'MsoListParagraphCxSpMiddle',
            'MsoListParagraphCxSpLast'
        ];
        for (let i = 0; i < standardListClasses.length; i++) {
            if (!isNOU(className) && standardListClasses[i].indexOf(className) >= 0) {
                return true;
            }
        }
        return false;
    }
    /* Determines if the list item should be reset */
    shouldResetListItem(listItem, index, collection, isStandardList) {
        return !isNOU(listItem) &&
            index !== 0 &&
            collection[index - 1].listType !== collection[index].listType &&
            !isStandardList;
    }
    /* Creates a paragraph element with content */
    createParagraphWithContent(item) {
        const paragraphElement = createElement('p', { className: 'MsoNoSpacing' });
        paragraphElement.innerHTML = item.content.join(' ');
        return paragraphElement;
    }
    /* Checks if this is a new root list */
    isNewRootList(item, listCount, formatOverride) {
        return item.nestedLevel === 1 &&
            (listCount === 0 || formatOverride !== item.listFormatOverride) &&
            item.content.length > 0;
    }
    /* Creates a root list element */
    createRootList(rootElement, item, paragraphElement) {
        const listElement = createElement(item.listType, { className: item.class });
        const listItem = createElement('li');
        listItem.appendChild(paragraphElement);
        listElement.appendChild(listItem);
        rootElement.appendChild(listElement);
        listElement.setAttribute('level', item.nestedLevel.toString());
        if (item.class !== 'msolistparagraph') {
            listElement.style.marginLeft = item.styleMarginLeft;
        }
        else {
            addClass([listElement], 'marginLeftIgnore');
        }
        listElement.style.listStyleType = item.listStyleTypeName;
        listItem.classList.add('e-current-list-item');
        return listElement;
    }
    /* Checks if this is a same level list item */
    isSameLevelList(item, currentLevel, formatOverride) {
        return item.nestedLevel === currentLevel && formatOverride === item.listFormatOverride;
    }
    /* Adds an item to a same level list */
    addToSameLevelList(item, listElement, paragraphElement, listItem, rootElement) {
        if (!isNOU(listItem) && !isNOU(listItem.parentElement) &&
            listItem.parentElement.tagName.toLowerCase() === item.listType) {
            // Add to existing list
            const newListItem = createElement('li');
            newListItem.classList.add('e-current-list-item');
            newListItem.appendChild(paragraphElement);
            listItem.parentElement.appendChild(newListItem);
            return listItem.parentElement;
        }
        else if (isNOU(listItem)) {
            // Create new list
            const newListElement = createElement(item.listType);
            newListElement.style.listStyleType = item.listStyleTypeName;
            const newListItem = createElement('li');
            newListItem.classList.add('e-current-list-item');
            newListItem.appendChild(paragraphElement);
            newListElement.appendChild(newListItem);
            newListElement.setAttribute('level', item.nestedLevel.toString());
            rootElement.appendChild(newListElement);
            return newListElement;
        }
        else {
            // Create new list at parent level
            const newListElement = createElement(item.listType);
            newListElement.style.listStyleType = item.listStyleTypeName;
            const newListItem = createElement('li');
            newListItem.classList.add('e-current-list-item');
            newListItem.appendChild(paragraphElement);
            newListElement.appendChild(newListItem);
            newListElement.setAttribute('level', item.nestedLevel.toString());
            listItem.parentElement.parentElement.appendChild(newListElement);
            return newListElement;
        }
    }
    /* Checks if this is a deeper nested list */
    isDeeperNestedList(item, currentLevel) {
        return item.nestedLevel > currentLevel;
    }
    /* Creates a nested list */
    createNestedList(item, listItem, paragraphElement, isStandardList, rootElement, currentNestingLevel) {
        let listElement;
        if (!isNOU(listItem)) {
            // Create nested list inside existing list item
            const levelDifference = item.nestedLevel - currentNestingLevel;
            for (let j = 0; j < levelDifference; j++) {
                listElement = createElement(item.listType);
                listItem.appendChild(listElement);
                listItem = createElement('li');
                // Set list-style-type: none for intermediate levels
                if (j !== levelDifference - 1 && levelDifference > 1) {
                    listItem.style.listStyleType = 'none';
                }
                listElement.appendChild(listItem);
            }
            listItem.classList.add('e-current-list-item');
            listItem.appendChild(paragraphElement);
            listElement.setAttribute('level', item.nestedLevel.toString());
            listElement.style.listStyleType = item.listStyleTypeName;
            return listElement;
        }
        else if (isStandardList) {
            // Create nested list for standard list type
            return this.createStandardNestedList(item, paragraphElement, rootElement);
        }
        else {
            // Create new root list with nesting level
            return this.createRootList(rootElement, item, paragraphElement);
        }
    }
    /* Creates a standard nested list */
    createStandardNestedList(item, paragraphElement, rootElement) {
        const initialNode = createElement(item.listType);
        let listItem = createElement('li');
        let listElement;
        initialNode.appendChild(listItem);
        initialNode.style.listStyleType = 'none';
        for (let j = 0; j < item.nestedLevel - 1; j++) {
            listElement = createElement(item.listType);
            listItem.appendChild(listElement);
            listItem = createElement('li');
            listElement.appendChild(listItem);
            listElement.style.listStyleType = 'none';
        }
        listItem.classList.add('e-current-list-item');
        listItem.appendChild(paragraphElement);
        rootElement.appendChild(initialNode);
        listElement.setAttribute('level', item.nestedLevel.toString());
        listElement.style.listStyleType = item.listStyleTypeName;
        return listElement;
    }
    /* Gets the last list item from a list element */
    getLastListItem(listElement) {
        return listElement.querySelector('li:last-child');
    }
    /* Checks if this is a top-level list */
    isTopLevelList(item) {
        return item.nestedLevel === 1;
    }
    /* Handles top-level list creation or reuse */
    handleTopLevelList(item, rootElement, paragraphElement) {
        let listElement;
        const lastChild = rootElement.lastChild;
        if (lastChild && lastChild.tagName.toLowerCase() === item.listType) {
            // Reuse existing list
            listElement = lastChild;
        }
        else {
            // Create new list
            listElement = createElement(item.listType);
            listElement.style.listStyleType = item.listStyleTypeName;
            rootElement.appendChild(listElement);
        }
        const listItem = createElement('li');
        listItem.appendChild(paragraphElement);
        listElement.appendChild(listItem);
        listElement.setAttribute('level', item.nestedLevel.toString());
        listItem.classList.add('e-current-list-item');
        return listElement;
    }
    /* Handles other nesting scenarios */
    handleOtherNestingScenarios(item, listItem, paragraphElement, currentFormatOverride) {
        let currentElement = listItem;
        let listElement;
        while (currentElement && currentElement.parentElement) {
            currentElement = currentElement.parentElement;
            const levelAttribute = currentElement.attributes.getNamedItem('level');
            if (levelAttribute) {
                const elementLevel = parseInt(levelAttribute.textContent, 10);
                if (elementLevel === item.nestedLevel && currentFormatOverride === item.listFormatOverride) {
                    // Same level and format - add to existing list
                    const newListItem = createElement('li');
                    newListItem.appendChild(paragraphElement);
                    currentElement.appendChild(newListItem);
                    newListItem.classList.add('e-current-list-item');
                    break;
                }
                else if (elementLevel === item.nestedLevel && currentFormatOverride !== item.listFormatOverride) {
                    // Same level but different format - create new list
                    this.createDifferentFormatList(item, currentElement, paragraphElement);
                    break;
                }
                else if (item.nestedLevel > elementLevel) {
                    // Deeper level - create nested list
                    listElement = createElement(item.listType);
                    const newListItem = createElement('li');
                    newListItem.appendChild(paragraphElement);
                    listElement.appendChild(newListItem);
                    currentElement.appendChild(listElement);
                    listElement.setAttribute('level', item.nestedLevel.toString());
                    listElement.style.listStyleType = item.listStyleTypeName;
                    newListItem.classList.add('e-current-list-item');
                    break;
                }
            }
        }
    }
    /* Creates a list with different format override */
    createDifferentFormatList(item, parentElement, paragraphElement) {
        let listElement = createElement(item.listType);
        let listItem = createElement('li');
        listElement.appendChild(listItem);
        if (item.nestedLevel > 1) {
            for (let k = 0; k < item.nestedLevel - 1; k++) {
                listItem.appendChild(listElement = createElement(item.listType));
                listItem = createElement('li');
                listElement.appendChild(listItem);
                listElement.style.listStyleType = 'none';
            }
        }
        listItem.appendChild(paragraphElement);
        listItem.classList.add('e-current-list-item');
        parentElement.appendChild(listElement);
        listElement.setAttribute('level', item.nestedLevel.toString());
        listElement.style.listStyleType = item.listStyleTypeName;
    }
    /* Applies styles and attributes to a list item */
    applyListItemStyles(listItem, item) {
        if (isNOU(listItem)) {
            return;
        }
        listItem.setAttribute('class', item.class);
        listItem.setAttribute('style', !isNOU(item.listStyle) ? item.listStyle : '');
    }
    /* Sets start attribute if needed */
    setStartAttributeIfNeeded(listElement, item) {
        const needsStartAttribute = !isNOU(item.start) &&
            item.start !== 1 && item.listType === 'ol';
        if (needsStartAttribute) {
            listElement.setAttribute('start', item.start.toString());
        }
    }
    /* Extracts list content from an element */
    getListContent(element) {
        const firstChild = element.firstElementChild;
        if (this.isImageList(firstChild)) {
            this.handleImageList(element);
        }
        else if (firstChild.childNodes.length > 0) {
            //Add to support separate list which looks like same list and also to add all tags as it is inside list
            this.handleTextList(element, firstChild);
        }
        this.listContents.push(element.innerHTML);
    }
    /* Checks if this is an image list */
    isImageList(firstChild) {
        return firstChild.textContent.trim() === '' &&
            !isNOU(firstChild.firstElementChild) &&
            firstChild.firstElementChild.nodeName === 'IMG';
    }
    /* Handles image list content */
    handleImageList(element) {
        const content = element.innerHTML.trim();
        this.listContents.push('');
        this.listContents.push(content);
    }
    /* Handles text list content */
    handleTextList(element, firstChild) {
        // Clean up list ignore tags
        this.cleanupListIgnoreTags(firstChild);
        // Clean up list order
        const listOrderElement = this.cleanupListOrder(firstChild);
        this.processListOrderElement(element, firstChild, listOrderElement);
    }
    /* Cleans up list ignore tags */
    cleanupListIgnoreTags(firstChild) {
        const listIgnoreTags = firstChild.querySelectorAll('[style*="mso-list"]');
        for (let i = 0; i < listIgnoreTags.length; i++) {
            const tag = listIgnoreTags[i];
            const style = tag.getAttribute('style').replace(/\n/g, '');
            tag.setAttribute('style', style);
        }
    }
    /* Cleans up list order element */
    cleanupListOrder(firstChild) {
        const listOrderCleanup = firstChild.querySelector('span[style*="mso-list"]');
        if (listOrderCleanup) {
            let style = listOrderCleanup.getAttribute('style');
            if (style) {
                style = style.replace(/\s*:\s*/g, ':');
                listOrderCleanup.setAttribute('style', style);
            }
        }
        return firstChild.querySelector('span[style="mso-list:Ignore"]');
    }
    /* Processes list order element */
    processListOrderElement(element, firstChild, listOrderElement) {
        const isEmptyMarkerSpan = isNOU(listOrderElement);
        listOrderElement = isEmptyMarkerSpan ? firstChild : listOrderElement;
        if (!isNOU(listOrderElement)) {
            let textContent = listOrderElement.textContent.trim();
            if (isEmptyMarkerSpan) {
                textContent = this.extractBulletMarker(listOrderElement, textContent);
            }
            this.listContents.push(textContent);
            if (!isEmptyMarkerSpan) {
                detach(listOrderElement);
            }
            this.removingComments(element);
            this.removeUnwantedElements(element);
        }
    }
    /* Extracts bullet marker from text content */
    extractBulletMarker(listOrderElement, textContent) {
        const bulletPattern = /^(\d{1,2}|[a-zA-Z]|[*#~•○■])(\.|\)|-)\s*/;
        const textContentMatch = textContent.match(bulletPattern);
        if (!isNOU(textContentMatch)) {
            const markerText = textContentMatch[0].trim();
            listOrderElement.textContent = listOrderElement.textContent.trim().substring(markerText.length).trim();
            return markerText;
        }
        return textContent;
    }
    /* Processes margins for different element types in the document */
    processMargin(clipboardDataElement) {
        this.processListItemMargins(clipboardDataElement);
        this.processListParentStructure(clipboardDataElement);
        this.processTableMargins(clipboardDataElement);
        this.processIgnoredNodeMargins(clipboardDataElement);
    }
    /* Processes and preserves list structure while removing absolute margins based on level 1 removal */
    processListParentStructure(clipboardDataElement) {
        const lists = clipboardDataElement.querySelectorAll('ul, ol');
        let level1Margin = 0;
        for (let i = 0; i < lists.length; i++) {
            const list = lists[i];
            const level = parseInt(list.getAttribute('level'), 10);
            const marginLeft = list.style.marginLeft;
            const currentMargin = !isNOU(marginLeft) && marginLeft !== '' ?
                (marginLeft.indexOf('pt') !== -1 ? parseFloat(marginLeft.split('pt')[0]) / 72 :
                    marginLeft.indexOf('px') !== -1 ? parseFloat(marginLeft.split('px')[0]) / 96 :
                        parseFloat(marginLeft.split('in')[0])) : 0;
            if (level === 1 || isNaN(level)) {
                level1Margin = currentMargin;
                list.style.marginLeft = '';
            }
            else if (level1Margin > 0 && currentMargin > 0) {
                const adjustedMargin = currentMargin - level1Margin;
                list.style.marginLeft = adjustedMargin > 0 ? adjustedMargin + 'in' : '';
            }
        }
    }
    /* Processes margins for list items */
    processListItemMargins(clipboardDataElement) {
        const listItems = clipboardDataElement.querySelectorAll('li');
        for (let i = 0; i < listItems.length; i++) {
            const listItem = listItems[i];
            // Clear margin-left for list items unless parent has 'marginLeftIgnore' class
            if (!isNOU(listItem.style.marginLeft) && !listItem.parentElement.classList.contains('marginLeftIgnore')) {
                listItem.style.marginLeft = '';
            }
        }
    }
    /* Processes margins for tables */
    processTableMargins(clipboardDataElement) {
        const tables = clipboardDataElement.querySelectorAll('table');
        for (let i = 0; i < tables.length; i++) {
            const table = tables[i];
            const marginLeft = table.style.marginLeft;
            // Clear negative margin-left values for tables
            if (!isNOU(marginLeft) && marginLeft.indexOf('-') >= 0) {
                table.style.marginLeft = '';
            }
        }
    }
    /* Processes margins for nodes with 'marginLeftIgnore' class */
    processIgnoredNodeMargins(clipboardDataElement) {
        const ignoredNodes = clipboardDataElement.querySelectorAll('.marginLeftIgnore li');
        for (let i = 0; i < ignoredNodes.length; i++) {
            const node = ignoredNodes[i];
            const marginLeft = node.style.marginLeft;
            // Adjust margin-left for ignored nodes
            if (!isNOU(marginLeft) && marginLeft !== '') {
                const marginValue = parseFloat(marginLeft.split('in')[0]);
                const adjustedValue = marginValue - 0.5;
                node.style.marginLeft = adjustedValue.toString() + 'in';
            }
        }
    }
    removeEmptyAnchorTag1(element) {
        const removableElement = element.querySelectorAll('a:not([href])');
        for (let j = removableElement.length - 1; j >= 0; j--) {
            const parentElem = removableElement[j].parentNode;
            while (removableElement[j].firstChild) {
                parentElem.insertBefore(removableElement[j].firstChild, removableElement[j]);
            }
            parentElem.removeChild(removableElement[j]);
        }
    }
    /* Removes empty anchor tags and preserves their contents */
    removeEmptyAnchorTag(clipboardDataElement) {
        // Select anchor tags without href attribute
        const emptyAnchors = clipboardDataElement.querySelectorAll('a:not([href])');
        // Process in reverse order to avoid index issues when removing elements
        for (let i = emptyAnchors.length - 1; i >= 0; i--) {
            const anchor = emptyAnchors[i];
            const parentNode = anchor.parentNode;
            // Move all children of the anchor to its parent
            while (anchor.firstChild) {
                parentNode.insertBefore(anchor.firstChild, anchor);
            }
            parentNode.removeChild(anchor);
        }
    }
    /* Determines the source of the clipboard content based on meta tags */
    findSource(containerElement) {
        const metaTags = containerElement.querySelectorAll('meta');
        for (let i = 0; i < metaTags.length; i++) {
            const metaTag = metaTags[i];
            const contentAttribute = metaTag.getAttribute('content');
            const nameAttribute = metaTag.getAttribute('name');
            const isMicrosoftGenerator = nameAttribute &&
                nameAttribute.toLowerCase().indexOf('generator') >= 0 &&
                contentAttribute && contentAttribute.toLowerCase().indexOf('microsoft') >= 0;
            if (isMicrosoftGenerator) {
                // Check against known paste sources
                for (let j = 0; j < PASTE_SOURCE.length; j++) {
                    const source = PASTE_SOURCE[j];
                    if (contentAttribute.toLowerCase().indexOf(source) >= 0) {
                        return source;
                    }
                }
            }
        }
        return 'html';
    }
    /* Handles OneNote-specific content by unwrapping empty list elements */
    handleOneNoteContent(clipboardDataElement) {
        const listElements = clipboardDataElement.querySelectorAll('ul, ol');
        for (let i = 0; i < listElements.length; i++) {
            const listElement = listElements[i];
            const hasNoListItems = listElement.querySelectorAll('li').length === 0;
            const hasChildNodes = listElement.childNodes.length > 0;
            // Unwrap list elements that have no list items but have other content
            if (hasNoListItems && hasChildNodes) {
                InsertMethods.unwrap(listElement);
            }
        }
    }
    processTableBorder(clipboardDataElement) {
        const tableElems = clipboardDataElement.querySelectorAll('table');
        const borderSeparatePattern = /mso-cellspacing\s*:\s*([^;]+);?/i;
        for (let i = 0; i < tableElems.length; i++) {
            const curTable = tableElems[i];
            const styleAttr = curTable.getAttribute('style');
            const isSeparateBorder = borderSeparatePattern.test(styleAttr);
            if (isSeparateBorder) {
                const cellSpacingValue = (styleAttr.match(borderSeparatePattern))[1];
                curTable.style.borderCollapse = 'separate';
                curTable.style.borderSpacing = cellSpacingValue;
            }
        }
    }
    /**
     * Cleans up resources when the component is destroyed
     *
     * @returns {void} - No return value
     * @public
     */
    destroy() {
        this.removeEventListener();
    }
}
